'use client'

import type { VariantProps } from 'class-variance-authority'

import * as React from 'react'
import { toast } from 'sonner'

import Copy from '../../icons/Copy'
import { cn } from '../../utils/cn'
import { Button, type buttonVariants } from '../_base/Button'

interface CopyButtonProps
  extends
    Omit<React.ComponentProps<typeof Button>, 'onClick' | 'onCopy'>,
    VariantProps<typeof buttonVariants> {
  value: string
  onCopy?: (value: string) => void
  successMessage?: string
  errorMessage?: string
  label?: string
}

export function CopyButton({
  value,
  onCopy,
  successMessage = 'Copied to clipboard',
  errorMessage = 'Failed to copy',
  disabled,
  label,
  tooltip,
  children,
  ...props
}: CopyButtonProps) {
  const handleCopy = React.useCallback(async () => {
    if (!value || disabled) return

    try {
      if (!navigator.clipboard) {
        console.warn('Clipboard API not supported')
        toast.error('Clipboard API not supported')
        return
      }

      await navigator.clipboard.writeText(value)
      toast.success(successMessage)
      onCopy?.(value)
    } catch (error) {
      console.error('Failed to copy text:', error)
      toast.error(errorMessage)
    }
  }, [value, disabled, successMessage, errorMessage, onCopy])

  // Wrappers like <TooltipTrigger asChild> can inject their own onClick via
  // React.cloneElement, so compose it with handleCopy instead of letting it
  // override us.
  const {
    className,
    onClick: incomingOnClick,
    ...rest
  } = props as typeof props & {
    onClick?: React.MouseEventHandler<HTMLButtonElement>
  }

  const accessibleLabel =
    label || (typeof tooltip === 'string' ? tooltip : undefined) || `Copy ${value} to clipboard`

  return (
    <Button
      {...rest}
      tooltip={tooltip}
      onClick={event => {
        incomingOnClick?.(event)
        if (!event.defaultPrevented) {
          void handleCopy()
        }
      }}
      disabled={disabled || !value}
      aria-label={accessibleLabel}
      // Skip the native title when a Radix tooltip is rendered to avoid a
      // duplicate browser tooltip stacking on top of it.
      title={tooltip ? undefined : accessibleLabel}
      className={cn(
        '[&>svg]:transition-colors',
        // On the neutral-surface ghost variant (the inline copy-address pattern) the
        // icon sits muted until hover, then lifts to foreground. On filled variants
        // (e.g. the default primary button) that muted gray clashes with the surface,
        // so let the icon inherit the button's own foreground token instead.
        // Scoped with [&>svg] so only the CopyIcon below gets the treatment — any
        // SVGs passed via children keep their own color.
        props.variant === 'ghost'
          ? '[&>svg]:text-muted-foreground hover:[&>svg]:text-foreground'
          : '[&>svg]:text-current',
        className
      )}
    >
      {children}
      <Copy className="size-5" />
    </Button>
  )
}
